import type { ScenarioSpec } from "./types";
import { officeUnderAttackScenario as OUA } from "./office-under-attack.scenario";

/**
 * Unpublished hub. The reception terminal from Office Under Attack offers
 * spear phishing (the existing course, skipping its intro and quiz), the
 * inbox chapter (PDF briefing + triage game), and a video slot.
 * Not listed on the public library.
 */
const VIDEO = "/cdn/sim/awareness-videos/understanding-phishing/understanding-phishing.mp4";
const POSTER = "/cdn/sim/awareness-videos/understanding-phishing/wide/endcard.jpg";
const SPEAR_HREF =
  "/?scenario=spear-phishing-osint&beat=b_rest_establish&return=spot-phishing&skipQuiz=1";
const OFFICE_HREF = "/?scenario=phishing-office&beat=b_to_monitor&return=spot-phishing&skipQuiz=1";

function beat(id: string) {
  const found = OUA.beats.find((item) => item.id === id);
  if (!found) throw new Error(`spot-phishing missing beat ${id}`);
  return found;
}

function device(id: string) {
  const found = OUA.devices.find((item) => item.id === id);
  if (!found) throw new Error(`spot-phishing missing device ${id}`);
  return found;
}

export const spotPhishingScenario: ScenarioSpec = {
  version: 1,
  id: "spot-phishing",
  title: "Spot a phishing email",
  summary: {
    goal: "Learn to spot phishing emails and lookalike links, and see how spear phishing and phone calls use personal details to make a lure believable.",
    youDo: "Watch a film, read a one-page briefing, sort an inbox into report, legitimate or verify, try a spear-phishing and a bank-call simulation if you want, then answer four questions.",
    duration: "Core 10-14 min, the two simulations add about 20 min",
    tags: ["email_phishing", "spear_phishing", "voice_phishing", "fake_login_pages", "oversharing", "verification"],
    audience: ["all_staff"],
    needs: ["sound"],
  },
  topic: "Recognise a phishing email, and see how spear phishing is built from public details",
  language: "en",
  passThreshold: 60,
  allowFreeExplore: false,
  voiceAttackers: OUA.voiceAttackers,
  scene: OUA.scene,

  devices: [
    {
      id: "phish_terminal",
      kind: "breachbrief",
      config: {
        noticeTag: "PHISHING",
        noticeKicker: "SECURITY AWARENESS",
        title: "Spot a phishing email",
        offLabel: "Switch on the welcome terminal",
        voiceId: "onyx",
        progressKey: "spot-phishing",
        idleImage: POSTER,
        lines: [
          "Phishing can arrive as an email, a message, or a phone call. Spear phishing uses details a person has shared in public.",
          "Watch the film, practise on the inbox, or step into a simulation. Anything you open is marked on this screen.",
        ],
        hubIntro: "Learn to spot phishing, then practise on an inbox and in two simulations.",
        exercisesKicker: "1 interactive exercise",
        exercisesLabel: "Practise",
        exercises: [
          {
            id: "inbox",
            label: "Learn about phishing",
            blurb: "Read the short briefing, then sort the inbox.",
            key: "inbox",
            completionBeatId: "b_cam_back",
            icon: "phishing",
            imageUrl: "/intro/img/phishing-office-card.webp",
            actionLabel: "Start",
            doneLabel: "Done",
          },
        ],
        finishLabel: "Finish the briefing",
        finishHint: "Continue to the quiz",
        finishEarlyHint: "You can finish now, or watch the film and try the simulations first.",
        hubVideo: {
          title: "Understanding phishing",
          blurb: "How phishing shows up, and what to do before you act.",
          src: VIDEO,
          poster: "/cdn/sim/awareness-videos/understanding-phishing/wide/01-inbox-email.jpg",
        },
        cardsTitle: "Simulations",
        cardsIntro: "Step into the attack. Each simulation brings you back to this terminal.",
        cards: [
          {
            id: "spear",
            label: "Learn about spear phishing",
            blurb: "Learn about spear phishing in this 3D simulation, from a public post to the lure.",
            key: "spear",
            href: SPEAR_HREF,
            completionBeatId: "b_spear_done",
            icon: "chapters",
            imageUrl: "/intro/img/spear-phishing-osint-card.webp",
            actionLabel: "Start",
            doneLabel: "Done",
          },
          {
            id: "voice",
            label: "Phishing and voice phishing",
            blurb: "See how attackers combine a phishing email with a voice call.",
            key: "voice",
            href: OFFICE_HREF,
            completionBeatId: "b_voice_done",
            icon: "phishing",
            imageUrl: "/intro/img/phishing-office-card.webp",
            actionLabel: "Start",
            doneLabel: "Done",
          },
        ],
      },
    },
    device("desktop_home"),
    device("inbox_triage"),
  ],

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Spot a phishing email",
      kicker: "Reception",
      guide: {
        title: "Start",
        body: "You start at an unattended reception in the NorthBay office. The welcome terminal offers a film on phishing, a short briefing and an inbox to sort at your desk, and two simulations you can step into.",
      },
    },
    { id: "b_cam_terminal", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_hub",
      type: "device",
      deviceId: "phish_terminal",
      guide: {
        title: "Reception terminal",
        body: "Switch on the welcome terminal. The film, the inbox, and the two simulations are on the screen.",
      },
      completeOn: { action: "continue" },
    },
    beat("b_cam_signin"),
    beat("b_desktop_pdf"),
    beat("b_desktop_mail"),
    beat("b_cam_inbox"),
    beat("b_inbox_triage"),
    beat("b_teach_inbox_risky"),
    beat("b_teach_inbox_safe"),
    { id: "b_cam_back", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_quiz",
      type: "quiz",
      title: "Check what you noticed",
      quiz: [
        {
          id: "q_channel",
          title: "Question 1",
          prompt: "A message says your account will be locked unless you act right now. What do you do?",
          options: [
            { id: "a", label: "Open the link so the account stays available", icon: "x" },
            { id: "b", label: "Pause, then open the real service yourself from its app or a bookmark", correct: true, icon: "check" },
            { id: "c", label: "Reply with your password so they can check the account", icon: "alert" },
          ],
          rationale: "Urgency is the lure. Phishing can arrive by email, message, or phone. Open the service yourself instead of using the link.",
        },
        {
          id: "q_spear",
          title: "Question 2",
          prompt: "What makes spear phishing different from a generic scam email?",
          options: [
            { id: "a", label: "It only works after the attacker already has your password", icon: "x" },
            { id: "b", label: "It uses details shared in public so the lure feels personal", correct: true, icon: "check" },
            { id: "c", label: "It can only be a phone call, never an email", icon: "alert" },
          ],
          rationale: "Public posts about work, travel, and interests give an attacker the details for a message that feels written for you.",
        },
        {
          id: "q_domain",
          title: "Question 3",
          prompt: "The link text says yourbank.com, but the address underneath is youbrank.com. What does that mean?",
          options: [
            { id: "a", label: "One swapped letter is close enough. Sign in.", icon: "x" },
            { id: "b", label: "It is a copy of the real site, so the password is safe there", icon: "alert" },
            { id: "c", label: "It is not your bank. Do not sign in.", correct: true, icon: "check" },
          ],
          rationale: "A lookalike domain is the trap. Check the real address before you type anything.",
        },
        {
          id: "q_voice",
          title: "Question 4",
          prompt: "A caller who sounds like your bank asks you to read the code on your authenticator. What do you do?",
          options: [
            { id: "a", label: "Read the code. A familiar voice proves it is the bank.", icon: "x" },
            { id: "b", label: "Hang up and call the number on your card. A voice can be copied from a few seconds of audio.", correct: true, icon: "check" },
            { id: "c", label: "Read only the first three digits", icon: "alert" },
          ],
          rationale: "That call is voice phishing. AI can clone a voice from about three seconds of audio, so sounding familiar is not proof.",
        },
      ],
    },
  ],

  edges: [
    { from: "b_intro", to: "b_cam_terminal" },
    { from: "b_cam_terminal", to: "b_hub" },
    { from: "b_hub", to: "b_cam_signin", on: "inbox" },
    { from: "b_cam_signin", to: "b_desktop_pdf" },
    { from: "b_desktop_pdf", to: "b_desktop_mail" },
    { from: "b_desktop_mail", to: "b_cam_inbox" },
    { from: "b_cam_inbox", to: "b_inbox_triage" },
    { from: "b_inbox_triage", to: "b_teach_inbox_risky", on: "risky" },
    { from: "b_inbox_triage", to: "b_teach_inbox_safe", on: "safe" },
    { from: "b_teach_inbox_risky", to: "b_cam_back" },
    { from: "b_teach_inbox_safe", to: "b_cam_back" },
    { from: "b_cam_back", to: "b_hub" },
    { from: "b_hub", to: "b_quiz", on: "finish" },
  ],

  initialBeatId: "b_intro",
};
